import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiClientError } from './client';

/**
 * One query client (spec §9.4).
 *
 * The error handling is centralised on purpose (MGA's `MutationCache` pattern): a mutation opts out
 * of the toast with `meta.silent` when it renders the failure itself — the grid does, because a
 * revision conflict opens a dialog rather than flashing a message.
 */

export type ToastFn = (message: string, severity: 'error' | 'warning') => void;

let toast: ToastFn | null = null;

/** Wired once, from the component tree that owns the toast host. */
export function setQueryToast(next: ToastFn | null): void {
  toast = next;
}

function report(error: unknown, silent: boolean): void {
  if (silent || !toast) return;
  if (error instanceof ApiClientError) {
    // The registration gate is handled by the admission gate, not by a toast.
    if (error.isNotRegistered) return;
    toast(error.message, error.status >= 500 || error.status === 0 ? 'error' : 'warning');
    return;
  }
  toast('Something went wrong.', 'error');
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // The editor is read constantly and written constantly; a short stale window keeps it
        // responsive without hammering the API on every focus change.
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiClientError) {
            // A 4xx will not become a 2xx by asking again.
            return error.status === 0 && failureCount < 2;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: 0 },
    },
    queryCache: new QueryCache({
      onError: (error, query) => report(error, Boolean(query.meta?.silent)),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        report(error, Boolean(mutation.meta?.silent)),
    }),
  });
}

/**
 * Query keys in one place, so an invalidation cannot miss a screen (spec §9.4).
 *
 * Naming rule: the plain name is the *prefix* a writer invalidates (`keysIn(scope)` clears every
 * page and filter combination), and a `…For(…)` name is the exact key one query reads. Inventing a
 * key at the call site is how a screen ends up showing what the user just deleted.
 */
export const queryKeys = {
  me: ['me'] as const,
  locales: ['locales'] as const,
  users: ['users'] as const,
  /** Prefix of every identity — what a user edit invalidates, since it may rename someone. */
  identities: ['identity'] as const,
  /** Who one actor oid is. Read through `UserLabel`, which batches every label on a screen. */
  identityFor: (oid: string) => ['identity', oid] as const,
  apiTokens: ['api-tokens'] as const,

  projects: ['projects'] as const,
  project: (code: string) => ['project', code] as const,
  projectStats: (code: string) => ['project', code, 'stats'] as const,

  packages: ['packages'] as const,
  package: (code: string) => ['package', code] as const,
  packageStats: (code: string) => ['package', code, 'stats'] as const,
  packageSubscribers: (code: string) => ['package', code, 'subscribers'] as const,

  keysIn: (scope: string) => ['keys', scope] as const,
  keys: (scope: string, filters: unknown, cursor: string | null) =>
    ['keys', scope, filters, cursor] as const,
  key: (scope: string, keyId: number) => ['key', scope, keyId] as const,
  /**
   * The global key search (§7.5A) across every project and package. Not under `keysIn`: no single
   * scope's writer can tell whether its change shows up here, so it relies on the stale window.
   */
  keySearch: ['key-search'] as const,
  keySearchFor: (query: unknown) => ['key-search', query] as const,

  // Scope paths (`/projects/x`), like every other scoped key; only projects publish (spec §8.4).
  targets: (scope: string) => ['targets', scope] as const,
  /** Prefix of every target query — what a target writer invalidates, wherever it was shown. */
  targetsEverywhere: ['targets'] as const,
  allTargets: ['targets', '/publish-targets'] as const,
  targetLocationFor: (projectCode: string) => ['target-location', projectCode] as const,
  releases: (scope: string) => ['releases', scope] as const,
  releasesFor: (scope: string, targetCode: string | null) =>
    ['releases', scope, targetCode] as const,
  release: (scope: string, releaseId: number) => ['release', scope, releaseId] as const,
  exportDiff: (scope: string, targetCode: string, locale: string | null) =>
    ['export-diff', scope, targetCode, locale] as const,

  missingKeys: (code: string) => ['missing-keys', code] as const,
  missingKeysFor: (code: string, status: string) => ['missing-keys', code, status] as const,

  /**
   * Open work across every scope — the bell and the dashboard (§9.2). One server-side aggregate, so
   * it shares no cache entry with the screens it links to: a writer that resolves a suggestion, a
   * missing key or a failed release invalidates this key too.
   */
  attention: ['attention'] as const,

  glossary: ['glossary'] as const,
  glossaryFor: (ownerType: string, ownerId: number | null) =>
    ['glossary', ownerType, ownerId] as const,

  aiProviders: (scope: string) => ['ai-providers', scope] as const,
  aiJobs: (scope: string) => ['ai-jobs', scope] as const,
  aiJobsFor: (scope: string, status: string) => ['ai-jobs', scope, status] as const,
  aiJob: (jobId: number) => ['ai-job', jobId] as const,

  /** Prefix of every Phrase sync query — status, runs, one run's detail and the conflicts. */
  phraseSync: ['phrase-sync'] as const,
  phraseSyncStatus: ['phrase-sync', 'status'] as const,
  phraseSyncRuns: ['phrase-sync', 'runs'] as const,
  phraseSyncRunFor: (runId: number) => ['phrase-sync', 'run', runId] as const,
  phraseSyncConflicts: ['phrase-sync', 'conflicts'] as const,

  imports: (scope: string) => ['imports', scope] as const,
  importBatch: (scope: string, batchId: number) => ['imports', scope, batchId] as const,
  /** The paged history list; `imports(scope)` is the prefix writers invalidate. */
  importHistoryFor: (scope: string) => ['imports', scope, 'history'] as const,

  // By the owner's scope path: a package and a project both have an inbox (ADR 0006), and their
  // codes may coincide.
  suggestions: (scope: string) => ['suggestions', scope] as const,
  suggestionsFor: (scope: string, status: string | undefined) =>
    ['suggestions', scope, status ?? null] as const,

  keyLinks: (code: string) => ['key-links', code] as const,
  keyLinksFor: (code: string, query: string) => ['key-links', code, query] as const,
  /** The link picker's hits; under `keyLinks` so linking refreshes which of them are linked. */
  keyLinkCandidatesFor: (code: string, query: string) =>
    ['key-links', code, { candidates: query }] as const,

  activity: (scope: string) => ['activity', scope] as const,
  /** The feed over one window, in hours (0 = everything). */
  activityFor: (scope: string, hours: number) => ['activity', scope, hours] as const,
};
